import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';

const PALETTES = {
    blue: {
        border: '#2563eb',
        softBorder: '#bfdbfe',
        buttonBg: '#eff6ff',
        panelBg: '#eff6ff',
        text: '#1e3a8a',
        title: '#1e40af',
        numberBg: '#2563eb',
    },
    amber: {
        border: '#f59e0b',
        softBorder: '#fde68a',
        buttonBg: '#fff7ed',
        panelBg: '#fffbeb',
        text: '#92400e',
        title: '#78350f',
        numberBg: '#d97706',
    },
};

function normalizeItem(item) {
    if (typeof item === 'string') return { text: item };
    return item || { text: '' };
}

function PageHelp({ items = [], sections = [], tone = 'blue', title = '운영 도움말', width = '420px' }) {
    const palette = PALETTES[tone] || PALETTES.blue;
    const [open, setOpen] = useState(false);
    const actionRef = useRef(null);
    const [compact, setCompact] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.innerWidth < 900;
    });
    const [anchor, setAnchor] = useState({ top: 0, left: 0 });

    useEffect(() => {
        const handleResize = () => setCompact(window.innerWidth < 900);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!open || typeof window === 'undefined') return undefined;

        const updateAnchor = () => {
            const rect = actionRef.current?.getBoundingClientRect();
            if (!rect) return;
            const fallbackWidth = Number.parseInt(width, 10) || 420;
            const left = Math.min(Math.max(rect.left, 16), Math.max(window.innerWidth - fallbackWidth - 16, 16));
            setAnchor({ top: rect.bottom + 8, left });
        };

        updateAnchor();
        window.addEventListener('resize', updateAnchor);
        window.addEventListener('scroll', updateAnchor, true);
        return () => {
            window.removeEventListener('resize', updateAnchor);
            window.removeEventListener('scroll', updateAnchor, true);
        };
    }, [open, width]);

    const popoverStyle = compact
        ? { ...getPopoverStyle(palette, width), ...popoverCompactStyle }
        : { ...getPopoverStyle(palette, width), top: anchor.top, left: anchor.left };
    const visibleSections = sections.length ? sections : [{ title: '', items }];

    return (
        <div ref={actionRef} style={helpActionStyle}>
            <button type="button" onClick={() => setOpen((prev) => !prev)} style={getToggleButtonStyle(palette)}>
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {open ? '도움말 숨기기' : '도움말 보기'}
            </button>
            {open && createPortal(
                <div style={popoverStyle}>
                    <div style={getTitleStyle(palette)}>{title}</div>
                    <div style={sectionListStyle}>
                        {visibleSections.map((section, sectionIndex) => {
                            const sectionItems = section?.items || [];
                            if (!sectionItems.length) return null;
                            return (
                                <div key={section?.title || sectionIndex} style={sectionStyle}>
                                    {section?.title && <div style={getSectionTitleStyle(palette)}>{section.title}</div>}
                                    <div style={detailStyle}>
                                        {sectionItems.map((item, index) => {
                                            const normalized = normalizeItem(item);
                                            return (
                                                <div key={normalized.key || normalized.text || index} style={itemWrapStyle}>
                                                    <div style={itemStyle}>
                                                        <span style={getNumberStyle(palette)}>{index + 1}</span>
                                                        <span>{normalized.text}</span>
                                                    </div>
                                                    {normalized.extra && <div style={extraStyle}>{normalized.extra}</div>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

const helpActionStyle = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
};

function getToggleButtonStyle(palette) {
    return {
        height: '30px',
        border: '1px solid ' + palette.border,
        borderRadius: '5px',
        backgroundColor: palette.buttonBg,
        color: palette.text,
        padding: '0 9px',
        fontSize: '12px',
        fontWeight: 800,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
    };
}

function getPopoverStyle(palette, width) {
    return {
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 100000,
        width,
        maxWidth: 'calc(100vw - 40px)',
        border: '1px solid ' + palette.softBorder,
        backgroundColor: palette.panelBg,
        color: palette.text,
        borderRadius: '6px',
        padding: '12px',
        fontSize: '13px',
        boxShadow: '0 12px 26px rgba(15, 23, 42, 0.18)',
    };
}

const popoverCompactStyle = {
    position: 'fixed',
    top: '92px',
    left: '16px',
    right: '16px',
    width: 'auto',
    maxWidth: 'none',
    maxHeight: 'calc(100vh - 116px)',
    overflow: 'auto',
};

function getTitleStyle(palette) {
    return {
        fontSize: '13px',
        fontWeight: 900,
        color: palette.title,
        marginBottom: '8px',
    };
}

const sectionListStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
};

const sectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
};

function getSectionTitleStyle(palette) {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        height: '22px',
        padding: '0 8px',
        borderRadius: '999px',
        backgroundColor: palette.buttonBg,
        border: '1px solid ' + palette.softBorder,
        color: palette.title,
        fontSize: '12px',
        fontWeight: 900,
    };
}

const detailStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    lineHeight: 1.55,
};

const itemWrapStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
};

const itemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
};

function getNumberStyle(palette) {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: '20px',
        height: '20px',
        borderRadius: '999px',
        backgroundColor: palette.numberBg,
        color: '#fff',
        fontSize: '12px',
        fontWeight: 900,
        lineHeight: 1,
    };
}

const extraStyle = {
    marginLeft: '28px',
};

export default PageHelp;
